import { TextField, TextAreaField, Button, Switch, Flexbox } from '@skf-internal/ui-components-react-legacy';
import { useIntl } from 'react-intl';
import { CreateManufacturerDto } from 'services/api/master-data.service';
import { useState, useEffect } from 'react';

interface ManufacturerFormProps {
  initialValues: CreateManufacturerDto;
  onSubmit: (values: CreateManufacturerDto) => void;
  onCancel: () => void;
}

const ManufacturerForm = ({ initialValues, onSubmit, onCancel }: ManufacturerFormProps) => {
  const intl = useIntl();
  const [formData, setFormData] = useState<CreateManufacturerDto>({
    ...initialValues,
    sWtg: initialValues.sWtg === 'Yes' ? 'Yes' : 'No',
    wtg: initialValues.wtg === 'Yes' ? 'Yes' : 'No',
    gearbox: initialValues.gearbox === 'Yes' ? 'Yes' : 'No',
    generator: initialValues.generator === 'Yes' ? 'Yes' : 'No'
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setFormData({
      ...initialValues,
      sWtg: initialValues.sWtg === 'Yes' ? 'Yes' : 'No',
      wtg: initialValues.wtg === 'Yes' ? 'Yes' : 'No',
      gearbox: initialValues.gearbox === 'Yes' ? 'Yes' : 'No',
      generator: initialValues.generator === 'Yes' ? 'Yes' : 'No'
    });
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const checked = (e.target as HTMLInputElement).checked; // Safely access checked for checkboxes
    const type = e.target.type;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked ? 'Yes' : 'No' }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        delete prev[name];
        return { ...prev };
      });
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.manufacturerName) {
      newErrors.manufacturerName = intl.formatMessage({ id: 'error.required' });
    }
    // Basic URL validation for websiteLink
    if (formData.websiteLink && !/^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/.test(formData.websiteLink)) {
      newErrors.websiteLink = intl.formatMessage({ id: 'error.invalidUrl' });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.manufacturerName' })}
        name="manufacturerName"
        value={formData.manufacturerName}
        onChange={handleChange}
        feHint={errors.manufacturerName}
        required
        className="mb-4"
      />
      <TextField
        feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.originalSourceName' })}
        name="originalSourceName"
        value={formData.originalSourceName || ''}
        onChange={handleChange}
        feHint={errors.originalSourceName}
        className="mb-4"
      />
      <TextField feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.country' })} name="country" value={formData.country || ''} onChange={handleChange} feHint={errors.country} className="mb-4" />
      <TextField feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.websiteLink' })} name="websiteLink" value={formData.websiteLink || ''} onChange={handleChange} feHint={errors.websiteLink} className="mb-4" />
      <Flexbox className="mb-4 justify-between">
        <Switch feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.sWtg' })} checked={formData.sWtg === 'Yes'} name="sWtg" onChange={handleChange} className="mb-4" />
        <Switch feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.wtg' })} checked={formData.wtg === 'Yes'} name="wtg" onChange={handleChange} className="mb-4" />
        <Switch feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.gearbox' })} checked={formData.gearbox === 'Yes'} name="gearbox" onChange={handleChange} className="mb-4" />
        <Switch feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.generator' })} checked={formData.generator === 'Yes'} name="generator" onChange={handleChange} className="mb-4" />
      </Flexbox>
      <TextAreaField feLabel={intl.formatMessage({ id: 'masterdata.manufacturer.form.remark' })} name="remark" value={formData.remark || ''} onChange={handleChange} feHint={errors.remark} className="mb-4" />

      <div className="flex justify-end space-x-4 mt-6">
        <Button onClick={onCancel}>{intl.formatMessage({ id: 'button.cancel' })}</Button>
        <Button type="submit">{intl.formatMessage({ id: 'button.save' })}</Button>
      </div>
    </form>
  );
};

export default ManufacturerForm;
